import React, { useEffect, useState } from "react";
import { Card, Collapse, Typography, Spin, Tag } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";

import { EventContainer } from "./EventContainer";
import { PageHeaderControl } from "../custom";
import { getFaqs } from "../../services/eventsGuideService";
import { SUPPORT_EMAIL } from "../../constants";
import { getApplicationVersion } from "../../utils/pwaVersionManager";
import { PrivacyStatment, TermsAndConditions } from "../usermanagement";

const { Panel } = Collapse;
const { Text, Link } = Typography;

/* =====================================================
   ENV / CONFIG
   ===================================================== */

const SHOW_USER_MANUALS = process.env.REACT_APP_ENABLE_MANUALS || false;
const POLICIES = [
  {
    key: "privacy-statement",
    title: "Privacy Statement",
    content: <PrivacyStatment />,
  },
  {
    key: "terms-and-conditions",
    title: "Terms and Conditions",
    content: <TermsAndConditions />,
  },
];

/* =====================================================
   COMPONENT
   ===================================================== */

export const EventsHelp = () => {
  const [faqs, setFaqs] = useState([]);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [loading, setLoading] = useState(false);
  const [expandedPolicies, setExpandedPolicies] = useState({});

  /* ================================
     LOAD FAQs
     ================================ */

  useEffect(() => {
    loadFaqs();
  }, []);

  const loadFaqs = async () => {
    setLoading(true);
    try {
      const res = await getFaqs();

      if (res?.status === 200) {
        const list = (res.data || [])
        setFaqs(list);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageHeaderControl title="Help">
      <EventContainer>

        {/* HELP SECTION */}
        <Card className="events-help__card">

          {/* FAQ */}
          <Collapse bordered={false} expandIconPosition="end">
            <Panel header="FAQ" key="faq">

              {/* Loading */}
              {loading && <Spin data-testid="events-faq-loading" />}

              {/* Empty */}
              {!loading && faqs.length === 0 && (
                <div data-testid="events-faq-empty" className="events-help__empty">
                  <Text type="secondary">
                    No FAQs available at the moment.
                  </Text>
                </div>
              )}

              {/* FAQ LIST */}
              {!loading && faqs.length > 0 && (
                <div data-testid="events-faq-section" className="events-help__faq-list">

                  {faqs.map((faq, index) => {
                    const isOpen = !!expandedFaqs[index];

                    return (
                      <div
                        data-testid="events-faq-card"
                        key={`events-faq-${index}`}
                        className="events-help__faq-card"
                      >
                        {/* HEADER */}
                        <button
                          type="button"
                          data-testid="events-faq-header"
                          aria-expanded={isOpen}
                          className="events-help__faq-card-header"
                          onClick={() =>
                            setExpandedFaqs(prev => ({
                              ...prev,
                              [index]: !prev[index],
                            }))
                          }
                        >
                          <span>{faq.question}</span>

                          <div className="events-help__faq-toggle">
                            {isOpen ? <MinusOutlined /> : <PlusOutlined />}
                          </div>
                        </button>

                        {isOpen && (
                          <div
                            data-testid="events-faq-body"
                            className="events-help__faq-card-body"
                          >
                            <div
                              className="events-help__faq-rich"
                              dangerouslySetInnerHTML={{
                                __html: faq.answer,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}

                </div>
              )}

            </Panel>
          </Collapse>
        </Card>

        {/* PRIVACY + TERMS */}
        <Card
          data-testid="events-privacy-conditions"
          className="events-help__card"
        >
          <h4 className="events-help__section-header">
            Privacy and Terms of Conditions
          </h4>

          <div className="events-help__faq-list">

            {POLICIES.map(({ key, title, content }) => {
              const isOpen = !!expandedPolicies[key];

              return (
                <div
                  key={key}
                  className="events-help__faq-card"
                >
                  {/* HEADER */}
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    className="events-help__faq-card-header"
                    onClick={() =>
                      setExpandedPolicies(prev => ({
                        ...prev,
                        [key]: !prev[key],
                      }))
                    }
                  >
                    <span>{isOpen? "": title}</span>
                    <div className="events-help__faq-toggle">
                      {isOpen ? <MinusOutlined /> : <PlusOutlined />}
                    </div>
                  </button>

                  {/* BODY */}
                  {isOpen && (
                    <div className="events-help__faq-card-body">
                      <div className="events-help__faq-rich">
                        {content}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

          </div>
        </Card>

        {/* USER MANUALS (FEATURE FLAG) */}
        {SHOW_USER_MANUALS && (
          <Card data-testid="events-user-manuals" className="events-help__card">
            <Collapse bordered={false} expandIconPosition="end">
              <Panel header="User Manuals" key="manuals">
                <ul className="events-help__manuals">
                  <li>
                    <Link href="#" target="_blank">
                      Event Management User Guide
                    </Link>
                  </li>
                  <li>
                    <Link href="#" target="_blank">
                      Attendance Capture Guide
                    </Link>
                  </li>
                </ul>
              </Panel>
            </Collapse>
          </Card>
        )}

        {/* CONTACT */}
        <Card data-testid="events-faq-contact" className="events-help__card">
          <h4 className="events-help__section-header">
            Do you have more questions?
          </h4>

          <Text>Please email us on: </Text>

          {SUPPORT_EMAIL ? (
            <Link href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </Link>
          ) : (
            <Text type="danger">
              Support email not configured
            </Text>
          )}

        </Card>

        <Card data-testid="events-faq-contact" className="events-help__card">
          <h4 className="events-help__section-header">
            App Information
          </h4>

          <Tag color="blue">Version {getApplicationVersion()}</Tag>
           

        </Card>

      </EventContainer>
    </PageHeaderControl>
  );
};
